import Toastify from 'toastify-js';

import { apiRequest } from '@common/api/utils';
import { retryWithRecaptchaV2Params } from '@common/utils/recaptcha';
import { isPresent } from '@common/utils/type';
import Validator from '@common/lib/validator';

import {
  getAttribution,
  setAttribution,
} from '@common/vanilla_ui/tracking/attribution';

import {
  Form,
  FormFlow,
  PhoneInput,
  Select,
} from '@common/vanilla_ui/general';
import { FlowTypes } from '@common/vanilla_ui/general/formFlow';
import {
  validatorSchema,
  PROGRAM_MAPPING,
  PROGRAM_PRODUCT_MATCHING,
} from './constants';
import { requestCallback, showErrorToast } from './utils';
import rcbTrackings from './trackings';

const formId = 'midfunnel-rcb-form';
const formFieldClass = 'form-field';
const fieldErrorClass = `${formFieldClass}__error`;
const fieldErrorHiddenClass = `${fieldErrorClass}--hidden`;
const inputBoxClass = `${formId}__input-box`;

const rcbOtpVerifyFormId = `${formId}-otp`;
const rcbPersonalDetailsFormId = `${formId}-personal-details`;

const rcbFormHandlers = {
  'midfunnel-rcb-form-phone': '_rcbFormPhoneHandler',
  'midfunnel-rcb-form-otp': '_rcbOtpVerifyHandler',
  'midfunnel-rcb-form-personal-details': '_rcbFormPersonalDetailsHandler',
};

class RcbForm extends FormFlow {
  constructor(id, containerId, mode = FlowTypes.modal, initialize = true) {
    const formType = FlowTypes.modal;
    super(formId, id, formType, mode);

    this.userDetails = {};
    this._setFormHandlers();
    this.userType = null;
    this.containerId = containerId;
    this.missingFields = {};
    this.validator = new Validator(validatorSchema);
    if (initialize) this.rcbFormFlowInitialize();
  }

  rcbFormFlowInitialize() {
    this._setisLoggedIn();
    rcbTrackings.initialize();
    this.initialize();
    this._trackFormCloseButton();
  }

  /* Private */

  _setisLoggedIn() {
    const formContainer = document.getElementById('midfunnel-rcb-form');
    this.isLoggedIn = formContainer.getAttribute('data-logged-in') === 'true';
  }

  // eslint-disable-next-line class-methods-use-this
  _trackFormCloseButton() {
    const formContainer = document.getElementById(`${this.containerId}`);
    const closeButton = formContainer?.querySelector(
      '[data-action="modal-close"][data-target="midfunnel-rcb-blocker-modal"]'
    );

    closeButton?.addEventListener('click', () => {
      rcbTrackings.trackActivity({
        clickType: 'Midfunnel Form Close',
        clickText: 'midfunnel_rcb_form',
      });
    });
  }

  _setFormHandlers() {
    Object.keys(rcbFormHandlers).forEach((mode) => {
      this.setFormHandler(mode, this[rcbFormHandlers[mode]]);
    });
  }

  _toggleInputField = (formEl, inputId, showInput) => {
    const inputEl = formEl?.querySelector(`#${inputId}`);
    if (!inputEl) return;

    if (!showInput) {
      inputEl.required = false;
      const parentElement = inputEl?.parentElement
        ?.parentElement?.parentElement;
      parentElement?.classList.add('hidden');
    } else {
      inputEl.required = true;
      inputEl.value = '';
      inputEl.disabled = false;
      const parentElement = inputEl?.parentNode;
      parentElement?.classList.remove('hidden');
    }
  }

  _hideBoxField = (formEl, inputId, wrapperClass) => {
    const inputEl = formEl?.querySelector(`#${inputId}`);
    if (!inputEl) return;

    inputEl.required = false;
    const parentElement = formEl?.querySelector(`.${wrapperClass}`);
    parentElement?.classList.add('hidden');
  };

  _hideSelectField = (formEl, selectId) => {
    const selectEl = formEl?.querySelector(`#${selectId}`);
    if (!selectEl) return;

    const parentEl = selectEl?.parentElement?.parentElement;
    const inputEl = selectEl?.querySelector(
      `input#input-${selectId}`,
    );
    if (!inputEl) return;

    inputEl.required = false;
    parentEl?.classList.add('hidden');
  }

  _toggleErrorField = ({
    errorEle,
    error,
    inputEls,
    addInputClass,
    removeInputClass,
  }) => {
    const errorField = errorEle;
    if (error) {
      errorField?.classList?.remove(fieldErrorHiddenClass);
      errorField.innerHTML = error;
    } else {
      errorField.innerHTML = '';
      errorField?.classList?.add(fieldErrorHiddenClass);
    }
    inputEls.forEach((input) => {
      input?.classList?.add(addInputClass);
      input?.classList?.remove(removeInputClass);
    });
  };

  _initializeProgramSelectInput() {
    const programInputId = `${formId}__program`;
    const formContainer = document.getElementById(`${this.containerId}`);
    const programInputEl = formContainer?.querySelector(`#${programInputId}`);
    if (programInputEl) {
      const programSelect = new Select(programInputId);
      this.programSelect = programSelect;
    }
  }

  _intializePhoneInput() {
    const phoneInput = new PhoneInput(`${formId}__phone`);
    this.phoneInput = phoneInput;
  }

  _initializeOrgPostionSelectInput() {
    const positionInputId = `${formId}-position`;
    const otherPositionInputId = `${formId}-other-position`;
    const formContainer = document.getElementById(`${this.containerId}`);
    const positionInputEl = formContainer?.querySelector(`#${positionInputId}`);
    const otherPositionInputEl = formContainer?.querySelector(
      `#${otherPositionInputId}`
    );
    const otherPositionFieldEl = otherPositionInputEl.parentElement;

    if (positionInputEl) {
      const positionSelect = new Select(positionInputId, { searchable: true });
      positionSelect.on('change', (value) => {
        if (value.toLowerCase() === 'other') {
          otherPositionFieldEl.classList.remove('hidden');
          otherPositionInputEl.required = true;
        } else {
          otherPositionFieldEl.classList.add('hidden');
          otherPositionInputEl.required = false;
        }
      });

      this.positionSelect = positionSelect;
    }
  }

  _validateOtpInputs(inputs, validatorKey) {
    let combinedValue = '';
    inputs.forEach(input => {
      if (isPresent(input.value)) {
        combinedValue += input.value;
      }
    });
    const boxValidator = {};
    boxValidator[validatorKey] = combinedValue;
    const error = this.validator.check(boxValidator);

    return isPresent(error) ? error[validatorKey] : null;
  }

  _handleOtpInputs(formEl, className, validatorKey) {
    const inputs = formEl?.querySelectorAll(
      `.${className}`,
    );

    const otpContainer = formEl?.querySelector(
      `.form-field[data-name='${validatorKey}']`,
    );

    const errorField = otpContainer?.querySelector(
      `.${fieldErrorClass}`,
    );

    const errorManipulation = () => {
      const error = this._validateOtpInputs(inputs, validatorKey);
      if (error) {
        this._toggleErrorField(
          {
            errorEle: errorField,
            inputEls: inputs,
            error,
            addInputClass: `${inputBoxClass}--error`,
            removeInputClass: `${inputBoxClass}--filled`,
          },
        );
      } else {
        this._toggleErrorField(
          {
            errorEle: errorField,
            inputEls: inputs,
            addInputClass: `${inputBoxClass}--filled`,
            removeInputClass: `${inputBoxClass}--error`,
          },
        );
      }
    };

    const totalOtpInputs = inputs.length;

    if (otpContainer) {
      const allInputs = Array.from(
        otpContainer.querySelectorAll('input'),
      );

      ['focus', 'blur'].forEach(listener => {
        otpContainer.addEventListener(listener, (event) => {
          if (event.target.tagName === 'INPUT') {
            errorManipulation();
          }
        });
      });

      otpContainer.addEventListener('keydown', (event) => {
        if (event.target.tagName === 'INPUT' && event.key === 'Backspace') {
          const index = allInputs.indexOf(event.target);

          if (index !== -1) {
            if (isPresent(allInputs[index].value)) {
              allInputs[index].value = '';
            } else if (index > 0) {
              allInputs[index - 1].focus();
            }
            errorManipulation();
          }
        }
      });

      otpContainer.addEventListener('input', (event) => {
        if (event.target.tagName === 'INPUT') {
          const index = allInputs.indexOf(event.target);

          allInputs[index].value = allInputs[index]
            .value.replace(/[^0-9]/g, '');
          if (allInputs[index].value !== '') {
            if (index !== totalOtpInputs - 1) allInputs[index + 1].focus();
          }
          errorManipulation();
        }
      });
    }
  }

  _setToastifyClass = (toastType) => `toastify-${toastType}`;

  _successfullRcbCallback = () => {
    rcbTrackings.formStatusTracking('success');

    const formContainer = document.getElementById(`${this.containerId}`);
    const requestCallbackSuccessEl = formContainer?.querySelector(
      '#midfunnel-rcb-blocker-modal-success',
    );
    const rcbLeftSection = formContainer?.querySelector(
      '#midfunnel-rcb-blocker-modal-left-section',
    );
    const requestCallbackFormEl = formContainer?.querySelector(
      '#midfunnel-rcb-blocker-modal-form',
    );
    const closeButtonContainer = formContainer?.querySelector(
      '[data-action="modal-close"][data-target="midfunnel-rcb-blocker-modal"]',
    );
    const blueCloseIcon = closeButtonContainer?.querySelector(
      '.blue-close-img'
    );
    const whiteCloseIcon = closeButtonContainer?.querySelector(
      '.white-close-img'
    );

    requestCallbackSuccessEl?.classList?.remove('hidden');
    requestCallbackFormEl?.classList?.add('hidden');
    rcbLeftSection?.classList?.add('hidden');
    blueCloseIcon?.classList?.add('hidden');
    whiteCloseIcon?.classList?.remove('hidden');
  };

  _setRcbAttribution = (program) => {
    setAttribution('rcb_midfunnel_banner', {
      program: PROGRAM_PRODUCT_MATCHING[program],
    });
  }

  _makeRcbRequest = (async ({ program, el }) => {
    try {
      const res = await requestCallback({
        userProgram: program,
        attribution: getAttribution(),
      });
      if (res?.success) {
        this._successfullRcbCallback();
      }
    } catch (error) {
      showErrorToast(error?.message, el);
    }
  });

  /* Form Handlers */

  _rcbFormPhoneHandler = (el) => {
    const requestOtpButton = el.querySelector(`.${formId}__request-otp-btn`);
    const whatsappConsentInputEl = el.querySelector(
      `#${formId}-whatsapp_consent`,
    );

    const generatePayload = ({
      phoneNumber, turnstileResponse, program,
    }) => {
      this.userDetails = {
        ...this.userDetails,
        phoneNumber,
        program,
      };

      this._setRcbAttribution(PROGRAM_MAPPING[program]
        || program);

      if (this.isLoggedIn) {
        return {
          user: {
            program: PROGRAM_MAPPING[program] || program,
          },
          attributions: getAttribution(),
        };
      } else {
        return {
          method: 'mobile',
          identifier: phoneNumber,
          whatsapp_consent: whatsappConsentInputEl?.value,
          'cf-turnstile-response': turnstileResponse,
          ...retryWithRecaptchaV2Params(el.id),
        };
      }
    };

    const whatsappConsentHandler = () => {
      if (whatsappConsentInputEl) {
        whatsappConsentInputEl.addEventListener('click', () => {
          if (whatsappConsentInputEl.value === 'whatsapp_consent_yes') {
            whatsappConsentInputEl.setAttribute('value', 'whatsapp_consent_no');
          } else {
            whatsappConsentInputEl.setAttribute(
              'value',
              'whatsapp_consent_yes',
            );
          }
        });
      }
    };

    const form = new Form(el, {
      method: 'POST',
      endpoint: this.isLoggedIn
        ? '/request-callback'
        : '/api/v3/auth/login-and-signup',
      createPayload: ({
        phone_number: phoneNumber,
        'cf-turnstile-response': turnstileResponse,
        program,
      }) => generatePayload({ phoneNumber, turnstileResponse, program }),
      validations: {
        phone_number: 'mobile',
        program: 'required',
      },
    });

    form.on('initialize', () => {
      whatsappConsentHandler();
      this._initializeProgramSelectInput();
      this._intializePhoneInput();
    });

    form.on('submitted', () => {
      if (this.isLoggedIn) {
        this._successfullRcbCallback();
      } else {
        this.switchMode(rcbOtpVerifyFormId);
        this.emit('update_phone_number');
      }
    });

    form.on('error', (error) => {
      if (error.response && error.response.status === 422) {
        window.fallbackToRecaptchaV2(el.id);
      }
      const message = error?.responseJson?.message
        || `Something went wrong!
      Refresh the page and try again`;

      showErrorToast(message, el);

      requestOtpButton.disabled = false;
    });

    form.initialize();

    return form;
  };

  _rcbOtpVerifyHandler = (el) => {
    const verifyOtpButton = el.querySelector(`.${formId}__verify-otp`);

    const allFieldsValid = () => {
      let valid = true;
      const inputs = el.querySelectorAll(`.${formId}__otp-input`);
      valid &&= !isPresent(this._validateOtpInputs(inputs, 'phone_otp'));

      return valid;
    };

    const getOtpValue = (className) => {
      const inputs = el.querySelectorAll(className);
      let combinedValue = '';
      inputs.forEach(input => {
        if (isPresent(input.value)) {
          combinedValue += input.value;
        }
      });

      return combinedValue;
    };

    const fetchUserData = async () => {
      try {
        const res = await apiRequest(
          'GET',
          '/api/v3/profile',
        );

        return res;
      } catch (error) {
        Toastify({
          text: error?.responseJson?.message
            || `Something went wrong!
              Refresh the page and try again`,
          className: this._setToastifyClass('danger'),
        }).showToast();
        return null;
      }
    };

    const getUserType = (data) => {
      const { data: { attributes } } = data;

      const requiredFields = {
        name: 'name',
        email: 'email',
        orgyear: 'orgyear',
        position: 'job_title',
      };

      const missingFields = [];
      const presentFields = {};

      Object.entries(requiredFields).forEach(([key, attr]) => {
        if (!attributes?.[attr]) {
          missingFields.push(key);
        } else {
          presentFields[key] = attributes[attr];
        }
      });

      const isProfileCompleted = missingFields.length === 0;

      return { missingFields, presentFields, isProfileCompleted };
    };

    const form = new Form(el, {
      method: 'POST',
      endpoint: '/api/v3/auth/verify-otp',
      createPayload: () => ({
        method: 'mobile',
        identifier: this.userDetails?.phoneNumber,
        code: getOtpValue(`.${formId}__otp-input`),
      }),
    });

    this.on('update_phone_number', () => {
      form.updateField('phone_number', this.userDetails?.phoneNumber);
    });

    form.on('initialize', () => {
      const otpVerifyBtn = el.querySelector(`.${formId}__verify-otp`);
      otpVerifyBtn.disabled = true;

      this._handleOtpInputs(el, `${formId}__otp-input`, 'otp_verification');
      const otpInputs = el.querySelectorAll(`.${formId}__otp-input`);

      otpInputs?.forEach((otpInput) => {
        otpInput.addEventListener('input', () => {
          if (isPresent(otpInput.value) && allFieldsValid()) {
            otpVerifyBtn.disabled = false;
          } else {
            otpVerifyBtn.disabled = true;
          }
        });
        otpInput.addEventListener('keydown', (event) => {
          if (event.key === 'Backspace') {
            if (isPresent(otpInput.value) && allFieldsValid()) {
              otpVerifyBtn.disabled = false;
            } else {
              otpVerifyBtn.disabled = true;
            }
          }
        });
      });
    });

    form.on('submit', () => {
      this._setRcbAttribution(PROGRAM_MAPPING[this.userDetails?.program]
        || this.userDetails?.program);
    });

    form.on('submitted', async (json) => {
      if (json?.success) {
        const data = await fetchUserData();
        const {
          missingFields,
          isProfileCompleted,
          presentFields,
        } = getUserType(data);

        if (isProfileCompleted) {
          this._makeRcbRequest({
            program: this.userDetails?.program,
            el,
          });
        } else {
          this.emit('hide_present_fields', missingFields, presentFields);
          this.switchMode(rcbPersonalDetailsFormId);
        }
      } else {
        const errormessage = json?.message
        || `Something went wrong!
      Refresh the page and try again`;

        showErrorToast(errormessage, el);
      }
    });

    form.on('error', (error) => {
      if (error.response && error.response.status === 422) {
        window.fallbackToRecaptchaV2(el.id);
      }
      const message = error?.responseJson?.message
        || `Something went wrong!
      Refresh the page and try again`;

      showErrorToast(message, el);
      verifyOtpButton.disabled = false;
    });

    form.initialize();

    return form;
  }

  _rcbFormPersonalDetailsHandler = (el) => {
    const requestCallbackBtn = el.querySelector(`.${formId}__rcb-btn`);

    const elementIdMapping = {
      name: {
        type: 'input',
        id: `${formId}__name`,
        hidden: false,
        payload_keys: ['name'],
      },
      email: {
        type: 'input',
        id: `${formId}__email`,
        hidden: false,
        payload_keys: ['email'],
      },
      orgyear: {
        type: 'box_input',
        id: `${formId}__orgyear`,
        wrapperClass: `${formId}__graduation`,
        hidden: false,
        payload_keys: ['orgyear'],
      },
      position: {
        type: 'select',
        id: `${formId}-position`,
        wrapperClass: `${formId}__position`,
        hidden: false,
        payload_keys: ['position'],
      },
    };

    const form = new Form(el, {
      method: 'PUT',
      endpoint: '/api/v3/profile',
      createPayload: ({
        email,
        position,
        orgyear,
        name,
      }) => {
        const user = {
          email,
          phone_number: this.userDetails.phoneNumber,
          job_title: position,
          orgyear,
          name,
        };

        this.userDetails = {
          ...this.userDetails,
          ...user,
        };

        return {
          ...user,
        };
      },
      validations: {
        name: 'required',
        email: 'email',
        position: 'required',
        orgyear: validatorSchema?.orgyear,
      },
    });

    this.on('hide_present_fields', (missingFields, presentFields) => {
      Object.entries(elementIdMapping).forEach(mapping => {
        const [fieldName, elementConfig] = mapping;

        if (!missingFields?.includes(fieldName)) {
          const { id, type, wrapperClass } = elementConfig;
          if (type === 'select') {
            this._hideSelectField(el, id);
          } else if (type === 'box_input') {
            this._hideBoxField(el, id, wrapperClass);
          } else {
            this._toggleInputField(el, id, false);
          }
          elementConfig.hidden = true;
          form.updateField(fieldName, presentFields[fieldName]);
        } else {
          const { id, type } = elementConfig;
          if (type === 'input') {
            this._toggleInputField(el, id, true);
          }
          elementConfig.hidden = false;
        }
      });
    });

    form.on('initialize', () => {
      this._initializeOrgPostionSelectInput();
    });

    form.on('submitted', async () => {
      requestCallbackBtn.disabled = false;
      this._makeRcbRequest({
        program: this.userDetails?.program,
        el,
      });
    });

    form.on('error', (error) => {
      if (error.response && error.response.status === 422) {
        window.fallbackToRecaptchaV2(el.id);
      }
      const message = error?.responseJson?.message
        || `Something went wrong!
      Refresh the page and try again`;

      showErrorToast(message, el);
    });

    form.initialize();

    return form;
  }
}

export default RcbForm;
